import { computedFn } from 'mobx-utils'

import { LocationType } from '~/client/graph'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'

// LBS Logic Helper Functions

/**
 * Gets all ancestor IDs for a location, stopping at ProjectLocation.
 */
function getAncestorIds(
  location: LocationBase,
  locationMap: { [key: string]: LocationBase },
): Set<string> {
  const ancestors = new Set<string>()
  const visited = new Set<string>([location.id])

  let current = location
  while (current.parent?.parentId) {
    const parent = locationMap[current.parent.parentId]
    if (!parent || isProjectLocation(parent) || visited.has(parent.id)) {
      break
    }
    visited.add(parent.id)
    ancestors.add(parent.id)
    current = parent
  }

  return ancestors
}

/**
 * Checks if a location is ProjectLocation (conceptually unparented)
 */
function isProjectLocation(location: LocationBase): boolean {
  return location.is(LocationType.ProjectLocation)
}

/**
 * Checks if a location has no parent (is unparented/root level)
 * Now treats ProjectLocation as conceptually unparented
 */
function isUnparented(
  location: LocationBase,
  locationMap: { [key: string]: LocationBase },
): boolean {
  // If the location itself is ProjectLocation, it's unparented
  if (!location.parent?.parentId || isProjectLocation(location)) {
    return true
  }

  // Get the parent location
  const parentLocation = locationMap[location.parent.parentId]
  if (!parentLocation || isProjectLocation(parentLocation)) {
    return true
  }

  return false
}

/**
 * LBS Logic check - checks if location is allowed based on hierarchy relationships.
 *
 * Rules applied:
 * 1. If location is an ancestor of the main location -> allow
 * 2. If location is a descendant of the main location -> allow
 * 3. If location is a sibling of the main location -> allow
 * 4. If location is unparented (or has ProjectLocation as parent) -> allow
 * 5. If location equals the main location -> allow
 * 6. Otherwise -> deny
 */
function isLocationAllowed(
  location: LocationBase,
  referenceLocation: LocationBase,
  referenceAncestors: Set<string>,
  locationMap: { [key: string]: LocationBase },
): boolean {
  // Rule 5: Same location
  if (
    location.id === referenceLocation.id ||
    location.is(referenceLocation.type)
  ) {
    return true
  }

  // Rule 4: Location is unparented (includes ProjectLocation as parent)
  if (isUnparented(location, locationMap)) {
    return true
  }

  // Has common parent
  if (location.hasCommonParent(referenceLocation)) {
    return true
  }

  // Rule 1: Location is ancestor of main location
  if (referenceAncestors.has(location.id)) {
    return true
  }

  // Rule 2: Location is descendant of main location
  const locationAncestors = getAncestorIds(location, locationMap)
  if (locationAncestors.has(referenceLocation.id)) {
    return true
  }

  // Rule 3: Location is sibling of main location
  for (const ancestorId of locationAncestors) {
    if (referenceAncestors.has(ancestorId)) {
      return true
    }
  }

  // Rule 6: Otherwise exclude
  return false
}

export function getLbsRestrictedIdsSet(
  allLocations: LocationBase[],
  referenceLocation: LocationBase,
  locationMap: { [key: string]: LocationBase },
): Set<string> {
  if (!referenceLocation || !allLocations?.length) {
    return new Set<string>()
  }

  const referenceAncestors = getAncestorIds(referenceLocation, locationMap)

  return allLocations.reduce((acc, location) => {
    if (
      !isLocationAllowed(
        location,
        referenceLocation,
        referenceAncestors,
        locationMap,
      )
    ) {
      acc.add(location.id)
    }
    return acc
  }, new Set<string>())
}

// Cached version - for reactive contexts where same parameters are used repeatedly
export const getLbsRestrictedIdsSetCached = computedFn(getLbsRestrictedIdsSet)
